import { UserRound, LogOut, Settings, User } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/hooks/useAuth";
import { PATHS } from "@/constants/page-paths";

type DropdownItem = {
  label: string;
  path?: string;
  icon: React.ReactNode;
  onClick?: () => void;
};

const dropdownItemList: DropdownItem[] = [
  {
    label: "Profile",
    path: "/profile",
    icon: <User size={18} />
  },
  {
    label: "Settings",
    path: "/settings",
    icon: <Settings size={18} />
  }
];

export const Navbar = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleUserAreaClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleLogout = () => {
    logout();
    navigate(PATHS.LOGIN);
    setDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="bg-white border-b border-gray-200 z-10">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-14">
        <div className="flex justify-end h-16 relative">
          <div className="flex items-center">
            <div className="relative" ref={dropdownRef}>
              <div
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                onClick={handleUserAreaClick}
              >
                <UserRound
                  size={40}
                  className="text-white rounded-full bg-blue-600 p-2"
                />
                <div className="ml-2 flex flex-col">
                  <span className="text-gray-600 text-base font-medium">{user?.email || 'User'}</span>
                  <span className="text-gray-700 text-base font-bold">{user?.role || 'Admin'}</span>
                </div>
              </div>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  {/* Dropdown Header */}
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                    <div className="font-bold text-gray-800">Welcome, {user?.role || 'Admin'}!</div>
                    <div className="text-xs text-gray-500">{user?.email || 'user@example.com'}</div>
                  </div>
                  {/* Dropdown Items */}
                  {dropdownItemList.map((item) => (
                    <div key={item.label}>
                      {item.path ? (
                        <Link
                          to={item.path}
                          className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <span className="mr-2 text-blue-500">
                            {item.icon}
                          </span>
                          <span className="text-gray-700">{item.label}</span>
                        </Link>
                      ) : (
                        <div
                          className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={item.onClick}
                        >
                          <span className="mr-2 text-blue-500">
                            {item.icon}
                          </span>
                          <span className="text-gray-700">{item.label}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Divider */}
                  <div className="border-b border-gray-100"></div>
                  {/* Logout Option */}
                  <div
                    className="flex items-center px-4 py-2 hover:bg-red-50 cursor-pointer transition-colors text-red-600"
                    onClick={handleLogout}
                  >
                    <span className="mr-2">
                      <LogOut size={18} />
                    </span>
                    <span>Logout</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}